/**
 * Admin > Metrics: window arithmetic ported from the design script (admin-usage-metrics.js).
 *
 * The API returns one value per calendar month for every base metric (`data.series[id]`, aligned
 * with `data.months`, 'YYYY-MM'). Derived metrics (churn rate, ARPA, LTV) are computed here from
 * their numerator / denominator series, so the page and the CSV export agree with the design:
 *   rate  = sum(num over the period) / den at the start of the period (previous month-end snapshot)
 *   arpa  = sum(num) / average(den) / months in the period   (per month)
 *   ltv   = arpa / (rate / months in the period)             (monthly churn)
 * A `null` month means "unknown" (e.g. a snapshot metric before it was recorded): the chips say n/a.
 * A definition with `series` is an alias: its numbers come from that other id.
 */
const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

export function monthLabel (ym) {
  if (!ym) return ''
  const [y, m] = String(ym).split('-')
  return MONTH_NAMES[parseInt(m, 10) - 1] + ' ' + y
}

export function shortMonthLabel (ym) {
  const [y, m] = String(ym).split('-')
  return MONTH_NAMES[parseInt(m, 10) - 1] + " '" + y.slice(2)
}

export function rangeLabel (months, s, e) {
  if (!months.length) return ''
  return s === e ? monthLabel(months[s]) : monthLabel(months[s]) + ' – ' + monthLabel(months[e])
}

function seriesKey (defs, id) {
  const def = defs && defs[id]
  return (def && def.series) || id
}

function base (data, id) {
  return (data && data.series && data.series[id]) || null
}

function cmBase (data, id, manager) {
  const byManager = data && data.cm && data.cm.series && data.cm.series[id]
  return (byManager && byManager[manager]) || null
}

function sumRange (arr, s, e) {
  let total = 0
  let seen = false
  for (let i = s; i <= e; i++) {
    const v = arr[i]
    if (v === null || v === undefined) continue
    total += v
    seen = true
  }
  return seen ? total : null
}

/**
 * Value of a metric over the months [s, e] (inclusive indexes into data.months).
 * `def` is a metric definition (metricsDefinitions.js); `lookup(id)` returns the series of a base metric.
 */
function windowValue (def, defs, lookup, s, e) {
  if (s < 0 || e < s) return null
  const len = e - s + 1
  if (def.agg === 'rate') {
    const num = lookup(def.num)
    const den = lookup(def.den)
    if (!num || !den) return null
    const n = sumRange(num, s, e)
    const d = s > 0 ? den[s - 1] : den[0]
    return n === null || !d ? null : n / d
  }
  if (def.agg === 'arpa') {
    const num = lookup(def.num)
    const den = lookup(def.den)
    if (!num || !den) return null
    const n = sumRange(num, s, e)
    const d = sumRange(den, s, e)
    return n === null || !d ? null : n / (d / len) / len
  }
  if (def.agg === 'ltv') {
    const arpa = windowValue(defs[def.num], defs, lookup, s, e)
    const churn = windowValue(defs[def.den], defs, lookup, s, e)
    const monthly = churn === null ? null : churn / len
    return arpa === null || !monthly ? null : arpa / monthly
  }
  const arr = lookup(def.id)
  if (!arr) return null
  if (def.agg === 'last') {
    const v = arr[e]
    return v === null || v === undefined ? null : v
  }
  return sumRange(arr, s, e)
}

export function winVal (data, defs, def, s, e) {
  return windowValue(def, defs, (id) => base(data, seriesKey(defs, id)), s, e)
}

export function cmWinVal (data, defs, def, manager, s, e) {
  return windowValue(def, defs, (id) => cmBase(data, seriesKey(defs, id), manager), s, e)
}

/**
 * One value per month of a metric: the stored series, or for a derived metric (churn rate, ARPA, LTV)
 * the formula applied to each month on its own, so trends and CSV columns exist for every row.
 */
export function metricSeries (data, defs, def) {
  if (!def.derived) return base(data, def.series || def.id)
  const n = ((data && data.months) || []).length
  const out = []
  for (let i = 0; i < n; i++) out.push(winVal(data, defs, def, i, i))
  return out
}

export function pct (cur, prev) {
  if (prev === null || prev === undefined || prev === 0 || cur === null || cur === undefined) return null
  return (cur - prev) / prev
}

/** Current, previous period and same period last year of a metric for the range [s, e]. */
export function calc (data, defs, def, s, e) {
  const len = e - s + 1
  const cur = winVal(data, defs, def, s, e)
  const prev = winVal(data, defs, def, s - len, e - len)
  const yoy = winVal(data, defs, def, s - 12, e - 12)
  return { def, cur, prev, yoy, dPrev: pct(cur, prev), dYoy: pct(cur, yoy) }
}

export function fmt (v, f) {
  if (v === null || v === undefined || Number.isNaN(v)) return '–'
  if (f === 'pct') return (v * 100).toFixed(1) + '%'
  const n = Math.round(v).toLocaleString('en-US')
  return f === 'usd' ? '$' + n : n
}

/** Relative change chip: { cls: 'good'|'bad'|'na', text, label }. */
export function chip (d, goodDown, label) {
  if (d === null || d === undefined) return { cls: 'na', text: 'n/a', label }
  const up = d >= 0
  const good = goodDown ? !up : up
  return { cls: good ? 'good' : 'bad', text: (up ? '▲' : '▼') + ' ' + Math.abs(d * 100).toFixed(1) + '%', label }
}

/** Percentage-point chip for rates (a rise in churn is bad). */
export function ppChip (cur, prev, label) {
  if (cur === null || cur === undefined || prev === null || prev === undefined) return { cls: 'na', text: 'n/a', label }
  const d = (cur - prev) * 100
  const up = d >= 0
  return { cls: up ? 'bad' : 'good', text: (up ? '▲' : '▼') + ' ' + Math.abs(d).toFixed(1) + ' pp', label }
}

export function deltaChip (c, label) {
  return c.def.fmt === 'pct' ? ppChip(c.cur, c.prev, label) : chip(c.dPrev, c.def.goodDown, label)
}

export function yearChip (c, label) {
  return c.def.fmt === 'pct' ? ppChip(c.cur, c.yoy, label) : chip(c.dYoy, c.def.goodDown, label)
}

/** Polyline + area points of the last 12 months ending at `end` (index), for an SVG of w x h. */
export function sparkline (arr, end, w, h) {
  if (!arr) return null
  const s = Math.max(0, end - 11)
  const d = arr.slice(s, end + 1).map((v) => (v === null || v === undefined ? 0 : v))
  if (d.length < 2) return null
  const mx = Math.max(...d)
  const mn = Math.min(...d)
  const pts = d.map((v, i) => [
    (i / (d.length - 1)) * w,
    h - 3 - (mx === mn ? 0.5 : (v - mn) / (mx - mn)) * (h - 8),
  ])
  const line = pts.map((p) => p[0].toFixed(1) + ',' + p[1].toFixed(1)).join(' ')
  return { line, area: '0,' + h + ' ' + line + ' ' + w + ',' + h }
}

/**
 * Bars of the monthly chart: the selected range (at least 12 months, so a one-month range still
 * shows context) with the same month last year next to each bar.
 */
export function barChart (data, def, s, e) {
  const arr = base(data, def.series || def.id) || []
  let start = s
  if (e - start < 5) start = Math.max(0, e - 11)
  const cur = []
  const prev = []
  for (let i = start; i <= e; i++) {
    cur.push(arr[i] === undefined ? null : arr[i])
    prev.push(i - 12 >= 0 && arr[i - 12] !== undefined ? arr[i - 12] : null)
  }
  const mx = Math.max(1, ...cur.filter((v) => v !== null), ...prev.filter((v) => v !== null))
  const n = cur.length
  const W = 100 / n
  const bars = []
  const labels = []
  for (let i = 0; i < n; i++) {
    const x = i * W
    const month = data.months[start + i]
    if (prev[i] !== null) {
      bars.push({ key: 'p' + i, cls: 'prev', left: x + W * 0.14, width: W * 0.34, height: (prev[i] / mx) * 100,
        title: monthLabel(data.months[start + i - 12]) + ': ' + fmt(prev[i], def.fmt) })
    }
    const inRange = start + i >= s && start + i <= e
    bars.push({ key: 'c' + i, cls: inRange ? '' : 'dim', left: x + W * 0.5, width: W * 0.34,
      height: cur[i] === null ? 0 : (cur[i] / mx) * 100, title: monthLabel(month) + ': ' + fmt(cur[i], def.fmt) })
    if (n <= 13 || i % 2 === 0) labels.push({ key: 'l' + i, left: i * W + W / 2, text: shortMonthLabel(month) })
  }
  return { bars, labels }
}

/** The months of a product's CSV: label + one column per month of the range + the three window values. */
export function productCsvRows (data, defs, product, s, e) {
  const ids = product.metrics.concat(product.total ? [product.total] : [])
  const head = ['Metric'].concat(data.months.slice(s, e + 1).map(monthLabel), ['Current', 'Prev period', 'Same period LY'])
  const rows = ids.map((id) => {
    const def = defs[id]
    const c = calc(data, defs, def, s, e)
    const arr = metricSeries(data, defs, def) || []
    const cells = [def.label]
    for (let i = s; i <= e; i++) cells.push(arr[i] === null || arr[i] === undefined ? '' : csvNumber(arr[i], def))
    cells.push(csvNumber(c.cur, def), csvNumber(c.prev, def), csvNumber(c.yoy, def))
    return cells
  })
  const managers = (data.cm && data.cm.managers) || []
  if (product.cm && managers.length) {
    rows.push([])
    rows.push(['By Customer Manager'])
    product.metrics.map((id) => defs[id]).filter((m) => !m.derived).forEach((m) => {
      managers.forEach((manager) => {
        const arr = cmBase(data, seriesKey(defs, m.id), manager.key) || []
        const cells = [m.label + ' (' + manager.name + ')']
        for (let i = s; i <= e; i++) cells.push(arr[i] === null || arr[i] === undefined ? '' : csvNumber(arr[i], m))
        cells.push(csvNumber(cmWinVal(data, defs, m, manager.key, s, e), m), '', '')
        rows.push(cells)
      })
    })
  }
  return [head].concat(rows)
}

function csvNumber (v, def) {
  if (v === null || v === undefined) return ''
  if (def.fmt === 'pct') return (v * 100).toFixed(2)
  return def.fmt === 'usd' ? Number(v).toFixed(2) : String(Math.round(v))
}

export function csvCell (value) {
  if (value === null || value === undefined) return '""'
  return '"' + String(value).replace(/"/g, '""') + '"'
}

export function downloadCsv (fileName, rows) {
  const text = rows.map((row) => row.map(csvCell).join(',')).join('\n')
  const link = document.createElement('a')
  const url = window.URL.createObjectURL(new Blob([text], { type: 'text/csv' }))
  link.download = fileName
  link.href = url
  link.style.display = 'none'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)
}

export function csvSlug (text) {
  return String(text).replace(/[^\w]+/g, '-').replace(/^-|-$/g, '')
}
